'use client';

/**
 * UnifiedBookingModal — modale di prenotazione unico (single-item, no carrello).
 * Supporta assetType 'boat' e 'excursion', riusando i flussi esistenti senza
 * duplicare la logica di prenotazione o pagamento.
 *
 * Fase 1: il corpo è type-specific (due body distinti) dentro una shell unica.
 * Fase 1.5: fusione totale degli step in un wizard unificato.
 *
 * Lazy-fetch ESCURSIONE: quando assetType==='excursion' e viene passato solo
 * `assetId` (id o slug) senza `asset` completo, il modale fetcha
 * GET /api/excursions/[assetId]/booking-data all'apertura.
 * Per le BARCHE non serve lazy: la card ha sempre il boat completo.
 */

import { useState, useMemo, useEffect } from 'react';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import DateRangePickerGrid from '@/components/ui/date-range-picker/DateRangePickerGrid';
import BookingWizardSidebarSteps from '@/components/booking-wizard/BookingWizardSidebarSteps';
import { useBookingCalendar } from '@/components/booking-wizard/useBookingCalendar';
import { useBoatBooking } from '@/components/booking-wizard/useBoatBooking';
import ExcursionBookingForm from '@/components/excursions/ExcursionBookingForm';
import ExcursionWizard from '@/components/booking/excursion/ExcursionWizard';
import BoatWizard from '@/components/booking/boat/BoatWizard';
import type { Boat } from '@/types';
import type { BlockedDateRange } from '@/components/ui/date-range-picker/types';
import type { ExcursionExtra } from '@/lib/excursion-extras';
import { transformExtras } from '@/lib/boat-detail-adapter';
import type { SeasonalPeriod } from '@/lib/excursion-seasonal-slots';

/* ── Asset types ──────────────────────────────────────────────────────────── */

export type BoatAsset = Boat;

export interface ExcursionAsset {
  id: string;
  slug: string;
  title?: string;
  pricingMode: string;
  price: number;
  maxCapacity: number;
  slots: Array<{
    id: string;
    start_at: string;
    end_at: string;
    capacity: number | null;
    booked_count: number;
    price_override: number | null;
  }>;
  pricingTiers?: Array<{ persons: number; price: number | string; label?: string; exclusive?: boolean }>;
  exclusivePrice?: number | null;
  exclusiveLabel?: string | null;
  exclusiveMaxPersons?: number | null;
  markupRate?: number;
  depositPercent?: number;
  requiresCardGuarantee?: boolean;
  availabilityMode?: string;
  seasonalPeriods?: SeasonalPeriod[];
  lastMinute?: { enabled?: boolean | null; daysBefore?: number | null; percent?: number | null };
  extras?: ExcursionExtra[];
}

/* ── Props ────────────────────────────────────────────────────────────────── */

interface UnifiedBookingModalProps {
  open: boolean;
  onClose: () => void;
  assetType: 'boat' | 'excursion';
  /** Asset completo. Se omesso (escursioni da catalogo), usare assetId per lazy-fetch. */
  asset?: BoatAsset | ExcursionAsset;
  /** Id o slug dell'escursione. Triggers lazy-fetch GET /api/excursions/[id]/booking-data. */
  assetId?: string;
  /** Date pre-selezionate (opzionale — passate dalla scheda dettaglio barca). */
  initialStartDate?: string;
  initialEndDate?: string;
  /** Date bloccate per la barca (opzionale — passate dalla scheda dettaglio). */
  boatBlockedDates?: BlockedDateRange[];
}

/* ── Helper ───────────────────────────────────────────────────────────────── */

const fmt = (d: Date | null) =>
  d ? d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : '—';

/* ── Sub-body: BARCA ─────────────────────────────────────────────────────── */

interface BoatBodyProps {
  boat: BoatAsset;
  blockedDates: BlockedDateRange[];
  initialStartDate?: string;
  initialEndDate?: string;
  isOpen: boolean;
}

function BoatBody({ boat, blockedDates, initialStartDate, initialEndDate, isOpen }: BoatBodyProps) {
  const hasInitialDates = !!(initialStartDate && initialEndDate);
  const [phase, setPhase] = useState<'dates' | 'wizard'>(hasInitialDates ? 'wizard' : 'dates');
  const booking = useBoatBooking();

  const cal = useBookingCalendar({
    isOpen,
    boatId: String(boat.id),
    blockedDates,
    initialStartDate: initialStartDate ?? '',
    initialEndDate: initialEndDate ?? '',
  });

  const start = cal.startDate ? new Date(cal.startDate + 'T12:00:00') : null;
  const end = cal.endDate ? new Date(cal.endDate + 'T12:00:00') : start;

  const handleContinue = () => {
    if (cal.startDate) setPhase('wizard');
  };

  return (
    <div data-cid="unified-booking-modal-boat" className="space-y-4">
      {phase === 'dates' ? (
        <div data-cid="ubm-boat-dates" className="space-y-4">
          <div className="rounded-2xl border border-slate-100 overflow-hidden">
            <DateRangePickerGrid {...cal.gridProps} />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-slate-500">
              {cal.startDate ? (
                <>
                  {fmt(start)} → {fmt(end)} ·{' '}
                  <b>
                    {cal.days} giorn{cal.days === 1 ? 'o' : 'i'}
                  </b>
                </>
              ) : (
                'Seleziona le date per continuare'
              )}
            </span>
            <button
              type="button"
              data-cid="ubm-boat-confirm-dates"
              disabled={!cal.startDate}
              onClick={handleContinue}
              className="px-6 py-2.5 bg-sky-600 text-white font-bold rounded-full hover:bg-sky-700 shadow-md disabled:opacity-50 disabled:shadow-none transition-all"
            >
              {cal.isRange ? 'Continua · queste date' : 'Continua'}
            </button>
          </div>
        </div>
      ) : (
        <div data-cid="ubm-boat-wizard" className="space-y-3">
          {(start || initialStartDate) && (
            <button
              type="button"
              onClick={() => setPhase('dates')}
              className="inline-flex items-center gap-1 text-xs font-bold text-sky-700 hover:text-sky-800"
            >
              <ArrowLeft size={13} />
              {fmt(start)} → {fmt(end)} · Modifica date
            </button>
          )}
          <BookingWizardSidebarSteps
            boat={boat}
            selectedStart={start}
            selectedEnd={end}
            onDateClick={() => setPhase('dates')}
            onBookingRequest={(d) => {
              if (start && end) booking.submit(boat, start, end, d);
            }}
            hideDatesStep
          />
        </div>
      )}
    </div>
  );
}

/* ── Sub-body: ESCURSIONE ─────────────────────────────────────────────────── */

function ExcursionBody({ asset }: { asset: ExcursionAsset }) {
  return (
    <div data-cid="unified-booking-modal-excursion">
      <ExcursionBookingForm
        excursionId={asset.id}
        excursionTitle={asset.title}
        slug={asset.slug}
        pricingMode={asset.pricingMode}
        price={asset.price}
        maxCapacity={asset.maxCapacity}
        slots={asset.slots}
        pricingTiers={asset.pricingTiers}
        exclusivePrice={asset.exclusivePrice}
        exclusiveLabel={asset.exclusiveLabel}
        exclusiveMaxPersons={asset.exclusiveMaxPersons}
        markupRate={asset.markupRate}
        depositPercent={asset.depositPercent}
        requiresCardGuarantee={asset.requiresCardGuarantee}
        availabilityMode={asset.availabilityMode}
        seasonalPeriods={asset.seasonalPeriods}
        lastMinute={asset.lastMinute}
        extras={asset.extras}
      />
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────────────────── */

export default function UnifiedBookingModal({
  open,
  onClose,
  assetType,
  asset: assetProp,
  assetId,
  initialStartDate,
  initialEndDate,
  boatBlockedDates = [],
}: UnifiedBookingModalProps) {
  /* ── Lazy-fetch escursione ─────────────────────────────────────────────── */
  const [fetchedAsset, setFetchedAsset] = useState<ExcursionAsset | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch solo se excursion + assetId + manca asset completo + modale aperto
    if (!open || assetType !== 'excursion' || assetProp || !assetId) return;
    let alive = true;
    setFetchLoading(true);
    setFetchError(null);
    fetch(`/api/excursions/${assetId}/booking-data`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => {
        if (!alive) return;
        if (j?.success && j?.asset) setFetchedAsset(j.asset as ExcursionAsset);
        else setFetchError('Dati non disponibili.');
      })
      .catch(() => { if (alive) setFetchError('Escursione non disponibile.'); })
      .finally(() => { if (alive) setFetchLoading(false); });
    return () => { alive = false; };
  }, [open, assetType, assetId, assetProp]);

  // Reset fetch state quando il modale si chiude o cambia assetId
  useEffect(() => {
    if (!open) { setFetchedAsset(null); setFetchError(null); }
  }, [open, assetId]);

  /* ── Completamento asset BARCA dal catalogo ────────────────────────────────
   * Le card catalogo passano una barca SENZA seasonalPrices: il breakdown prezzo
   * (mezza giornata stagionale) userebbe la flat barca → totale diverso dal
   * server → submit rifiutato ("Price calculation error"). Se mancano le
   * stagioni, completa l'asset dal detail (?with_markup=1, stessa vista prezzi). */
  const [boatFull, setBoatFull] = useState<BoatAsset | null>(null);
  // F2 prezzo-per-porto: punto scelto nel wizard → rifetcha i prezzi di quel layer.
  const [bookingPoint, setBookingPoint] = useState('');
  useEffect(() => {
    if (!open || assetType !== 'boat') { setBoatFull(null); return; }
    const b = assetProp as (BoatAsset & { seasonalPrices?: unknown[]; departure_points?: unknown[] }) | null;
    const id = b?.id ?? assetId;
    // Fetch del detail se manca ANCHE UNO tra: stagioni (per il prezzo) e departure_points
    // (per la scelta punto al booking). Il catalogo non porta i departure_points → senza
    // questo il fetch veniva saltato per le barche con stagioni e lo step spariva.
    // F2: con un punto scelto rifetcho SEMPRE (per avere i prezzi del layer del porto).
    const hasSeasons = Array.isArray(b?.seasonalPrices) && b.seasonalPrices.length > 0;
    const hasDeparturePoints = Array.isArray(b?.departure_points);
    if (!id || (hasSeasons && hasDeparturePoints && !bookingPoint)) return;
    let alive = true;
    fetch(`/api/boats/${id}?with_markup=1${bookingPoint ? `&departure_point_id=${bookingPoint}` : ''}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => { if (alive && j?.data) setBoatFull(j.data as BoatAsset); })
      .catch(() => { /* fallback: asset del catalogo com'è */ });
    return () => { alive = false; };
  }, [open, assetType, assetProp, assetId, bookingPoint]);

  /* ── Asset risolto ─────────────────────────────────────────────────────── */
  const asset = (assetType === 'excursion'
    ? (assetProp ?? fetchedAsset)
    : (boatFull
        // /api/boats/[id] ritorna extra con shape annidata (boat_extras.custom_price,
        // niente price/priceType/mandatory top-level) → transformExtras li appiattisce
        // come li vuole BoatWizard: senza, price=undefined → NaN a display + addebito 0.
        ? { ...(assetProp as object ?? {}), ...(boatFull as object), extras: transformExtras((boatFull as { extras?: unknown[] }).extras ?? []) }
        : assetProp)) as BoatAsset | ExcursionAsset | null;

  const title = useMemo(() => {
    if (assetType === 'boat' && asset) return `Prenota · ${(asset as BoatAsset).name}`;
    if (assetType === 'excursion' && asset) return `Prenota · ${(asset as ExcursionAsset).title ?? 'escursione'}`;
    return 'Prenota';
  }, [assetType, asset]);

  /* Modal size: xl per barca (calendario largo), lg per escursione */
  const size = assetType === 'boat' ? 'xl' : 'lg';

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={title}
      size={size}
      usePortal
      closeOnEsc
      closeOnOverlayClick
      bodyClassName="p-4 md:p-6"
      data-cid="unified-booking-modal"
    >
      {/* Loading state (lazy-fetch escursione) */}
      {fetchLoading && (
        <div data-cid="ubm-loading" className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
          <Loader2 size={32} className="animate-spin text-sky-500" />
          <p className="text-sm font-medium">Caricamento dati prenotazione…</p>
        </div>
      )}

      {/* Error state */}
      {fetchError && !fetchLoading && (
        <div data-cid="ubm-error" className="flex flex-col items-center justify-center py-16 gap-3 text-slate-500">
          <AlertCircle size={32} className="text-red-400" />
          <p className="text-sm font-medium">{fetchError}</p>
        </div>
      )}

      {/* Content */}
      {!fetchLoading && !fetchError && asset && (
        assetType === 'boat' ? (
          <BoatWizard
            boat={asset as BoatAsset}
            blockedDates={boatBlockedDates}
            initialStartDate={initialStartDate}
            initialEndDate={initialEndDate}
            onDeparturePointChange={setBookingPoint}
          />
        ) : (
          <ExcursionWizard asset={asset as ExcursionAsset} />
        )
      )}
    </Modal>
  );
}
